import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../config/theme';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ChecklistDateTimeFieldProps {
  field: { id: string; nome: string };
  value?: string;
  onChange: (val: string) => void;
}

/**
 * Campo de data/hora para checklist (web)
 * Usa react-datepicker dentro de um Modal customizado.
 * Mostra valor formatado e ícone de calendário.
 */
const injectDatepickerStyles = () => {
  if (document.getElementById('datepicker-custom-styles')) return;
  const style = document.createElement('style');
  style.id = 'datepicker-custom-styles';
  style.innerHTML = `
    .react-datepicker {
      border-radius: ${theme.borderRadius.md}px;
      border: none;
      box-shadow: 0 2px 12px rgba(79, 115, 225, 0.10);
      font-family: ${theme.fontFamily?.regular || 'system-ui, sans-serif'};
      background: ${theme.colors.background.primary};
    }
    .react-datepicker__header {
      background: ${theme.colors.background.primary};
      border-bottom: 1px solid ${theme.colors.border.light};
      border-radius: ${theme.borderRadius.md}px ${theme.borderRadius.md}px 0 0;
    }
    .react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header {
      color: ${theme.colors.text.primary};
      font-size: ${theme.fontSizes.md}px;
      font-weight: ${theme.fontWeights.bold};
    }
    .react-datepicker__day, .react-datepicker__time-list-item {
      color: ${theme.colors.text.primary};
      font-size: ${theme.fontSizes.sm}px;
      border-radius: ${theme.borderRadius.sm}px;
      transition: background 0.15s;
    }
    .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected, .react-datepicker__time-list-item--selected {
      background: ${theme.colors.primary.default};
      color: #fff;
    }
    .react-datepicker__day:hover, .react-datepicker__time-list-item:hover {
      background: ${theme.colors.primary.light};
      color: #fff;
    }
    .react-datepicker__day--today {
      border-bottom: 2px solid ${theme.colors.secondary.default};
    }
    .react-datepicker__year-read-view, .react-datepicker__month-read-view {
      border-radius: ${theme.borderRadius.sm}px;
      border: 1px solid ${theme.colors.border.light};
      background: ${theme.colors.background.secondary};
      padding: 4px 12px;
      font-size: ${theme.fontSizes.sm}px;
      color: ${theme.colors.text.primary};
      margin: 0 4px;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
      box-shadow: 0 1px 2px rgba(79, 115, 225, 0.06);
      display: inline-flex;
      align-items: center;
    }
    .react-datepicker__year-read-view:hover, .react-datepicker__month-read-view:hover {
      border-color: ${theme.colors.primary.default};
      background: ${theme.colors.background.tertiary};
    }
    .react-datepicker__year-dropdown, .react-datepicker__month-dropdown {
      background: ${theme.colors.background.primary};
      border-radius: ${theme.borderRadius.sm}px;
      border: 1px solid ${theme.colors.border.light};
      box-shadow: 0 2px 8px rgba(79, 115, 225, 0.08);
      margin-top: 2px;
      min-width: 100px;
      overflow: hidden;
    }
    .react-datepicker__year-option, .react-datepicker__month-option {
      color: ${theme.colors.text.primary};
      font-size: ${theme.fontSizes.sm}px;
      padding: 6px 12px;
      border-radius: ${theme.borderRadius.xs}px;
      transition: background 0.15s;
      cursor: pointer;
    }
    .react-datepicker__year-option:hover, .react-datepicker__month-option:hover {
      background: ${theme.colors.primary.light};
      color: #fff;
    }
    .react-datepicker__triangle {
      display: none;
    }
    .react-datepicker__year-read-view--down-arrow, .react-datepicker__month-read-view--down-arrow {
      border-top: 6px solid ${theme.colors.primary.default};
      border-right: 6px solid transparent;
      border-left: 6px solid transparent;
      margin-left: 8px;
      margin-bottom: 2px;
    }
  `;
  document.head.appendChild(style);
};

const ChecklistDateTimeField: React.FC<ChecklistDateTimeFieldProps> = ({ field, value, onChange }) => {
  // Estado para controlar abertura do modal
  const [open, setOpen] = useState(false);
  // Estado local para data/hora selecionada
  const [selected, setSelected] = useState<Date | null>(value ? new Date(value) : new Date());

  // Formata valor para exibição (DD/MM/YYYY - HH:mm:ss)
  const formatValue = (val?: string) => {
    if (!val) return 'Selecionar data e hora...';
    try {
      const d = new Date(val);
      return format(d, 'dd/MM/yyyy - HH:mm:ss', { locale: ptBR });
    } catch {
      return val;
    }
  };

  // Ao abrir, já seleciona data/hora atual se não houver valor
  const handleOpen = () => {
    setSelected(value ? new Date(value) : new Date());
    setOpen(true);
  };

  // Confirma seleção
  const handleConfirm = () => {
    if (selected) {
      onChange(selected.toISOString());
    }
    setOpen(false);
  };

  // Cancela seleção
  const handleCancel = () => {
    setOpen(false);
  };

  // Injeta o CSS customizado do datepicker ao montar
  useEffect(() => {
    injectDatepickerStyles();
  }, []);

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{field.nome}</Text>
      <TouchableOpacity
        style={[styles.selectButton, open && styles.selectButtonActive]}
        onPress={handleOpen}
        activeOpacity={0.85}
      >
        <Text style={styles.valueText}>{formatValue(value)}</Text>
        <Ionicons name="calendar" size={20} color={theme.colors.primary.default} style={{ marginLeft: 8, flexShrink: 0 }} />
      </TouchableOpacity>
      {/* Modal customizado para o picker */}
      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={handleCancel}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <DatePicker
              selected={selected ?? new Date()}
              onChange={date => setSelected(date as Date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={5}
              dateFormat="dd/MM/yyyy - HH:mm"
              locale={ptBR}
              inline
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.cancelButton} onPress={handleCancel}>
                <Text style={styles.cancelButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.confirmButton} onPress={handleConfirm}>
                <Text style={styles.confirmButtonText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Estilos minimalistas e elegantes, usando o theme do app
const styles = StyleSheet.create({
  container: {
    marginVertical: theme.spacing.sm,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.components.checklistEditor.spacing.labelGap,
    fontFamily: theme.fontFamily?.medium || 'system-ui, sans-serif',
  },
  selectButton: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 44,
    ...theme.shadows.sm,
  },
  selectButtonActive: {
    borderColor: theme.colors.primary.default,
    ...theme.shadows.md,
  },
  valueText: {
    flex: 1,
    minWidth: 0,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    fontFamily: theme.fontFamily?.regular || 'system-ui, sans-serif',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay.semi,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: 28,
    minWidth: 340,
    alignItems: 'center',
    ...theme.shadows.md,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 18,
    width: '100%',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.background.secondary,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    marginRight: 0,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    alignItems: 'center',
    marginLeft: 0,
  },
  cancelButtonText: {
    color: theme.colors.text.primary,
    fontWeight: 'bold',
    fontFamily: theme.fontFamily?.medium || 'system-ui, sans-serif',
  },
  confirmButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontFamily: theme.fontFamily?.medium || 'system-ui, sans-serif',
  },
});

export default ChecklistDateTimeField; 